import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1773966071353-0956f870e034?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxzdGVlbCUyMGZhY3RvcnklMjBkYXJrJTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3ODgxMDk1Mjl8MA&ixlib=rb-4.1.0&q=85";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const yMonogram = useTransform(scrollY, [0, 900], [0, 180]);
  const yBg = useTransform(scrollY, [0, 900], [0, 120]);
  const opacityFade = useTransform(scrollY, [0, 600], [1, 0.2]);

  return (
    <section
      id="top"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden border-b border-[#262626]"
      data-testid="hero-section"
    >
      <motion.div className="absolute inset-0" style={{ y: yBg }}>
        <img
          src={HERO_IMG}
          alt="Steel warehouse interior"
          className="w-full h-full object-cover opacity-25 grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/30" />
      </motion.div>

      <motion.div
        className="absolute right-[-8%] md:right-[2%] top-[8%] md:top-[4%] w-[55vw] md:w-[32vw] max-w-[520px] pointer-events-none"
        style={{ y: yMonogram, opacity: opacityFade }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="apex-flare absolute left-1/2 -translate-x-1/2 top-[6%] w-24 h-24 rounded-full" />
          <img
            src="/logo-monogram.png"
            alt="Ameer Iron & Steels steel monogram"
            className="w-full h-auto blend-screen"
            data-testid="hero-monogram"
          />
        </motion.div>
      </motion.div>

      <div className="relative z-10 px-6 md:px-12 pb-16 md:pb-24 pt-40">
        <div className="mb-6 overflow-hidden">
          <motion.p
            initial={{ y: "120%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="text-xs uppercase tracking-[0.35em] text-[#a3a3a3]"
            data-testid="hero-tagline"
          >
            New Steel Supply /// Iron, Plastic &amp; Vehicle Scrap
          </motion.p>
        </div>

        <h1 className="font-display uppercase leading-[0.9] tracking-tight text-[16vw] md:text-[11vw]" data-testid="hero-headline">
          <Line delay={0.35}>
            <span className="text-steel">Ameer</span>
          </Line>
          <Line delay={0.5}>
            <span className="text-steel">Iron &amp; Steels</span>
          </Line>
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <Line delay={0.7}>
            <p className="max-w-md text-sm md:text-base text-[#a3a3a3] leading-relaxed" data-testid="hero-subtext">
              MS pipes, TMT rods, channels, flats and GI items — supplied straight
              from the yard. And we buy your scrap at honest weights and honest rates.
            </p>
          </Line>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="flex items-center gap-6"
          >
            <button
              onClick={() => scrollToSection("#quote")}
              className="bg-[#f5f5f5] text-[#050505] text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-[#d4d4d4] transition-colors duration-300"
              data-testid="hero-quote-button"
            >
              Request a Quote
            </button>
            <div className="flex items-center gap-2 text-[#525252] text-xs uppercase tracking-[0.25em]">
              Scroll
              <motion.span
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              >
                <ArrowDown size={14} />
              </motion.span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
